import React, { memo, forwardRef } from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Primary button component used across the app.
 *
 * Variants: primary, secondary, outline, danger, success, warning, ghost
 * Sizes: sm, md, lg, xl
 * Features: loading spinner, icon (left/right), fullWidth
 */
const OptimizedButton = memo(forwardRef(({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  icon,
  iconPosition = 'left',
  fullWidth = false,
  className = '',
  onClick,
  type = 'button',
  ...props
}, ref) => {
  const buttonClasses = React.useMemo(() => {
    const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

    const variantClasses = {
      primary: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
      secondary: 'bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-500',
      outline: 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-blue-500',
      danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
      success: 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500',
      warning: 'bg-yellow-500 text-white hover:bg-yellow-600 focus:ring-yellow-500',
      ghost: 'bg-transparent text-gray-700 hover:bg-gray-100 focus:ring-gray-500',
    };

    const sizeClasses = {
      sm: 'px-3 py-1.5 text-sm gap-1.5',
      md: 'px-4 py-2 text-sm gap-2',
      lg: 'px-6 py-3 text-base gap-2',
      xl: 'px-8 py-4 text-lg gap-3',
    };

    const widthClass = fullWidth ? 'w-full' : '';

    return `${baseClasses} ${variantClasses[variant] || variantClasses.primary} ${sizeClasses[size]} ${widthClass} ${className}`;
  }, [variant, size, fullWidth, className]);

  const isDisabled = disabled || loading;

  const iconEl = loading ? <Loader2 className="w-4 h-4 animate-spin" /> : icon;

  return (
    <button
      ref={ref}
      type={type}
      className={buttonClasses}
      disabled={isDisabled}
      onClick={onClick}
      {...props}
    >
      {iconEl && iconPosition === 'left' && iconEl}
      {children}
      {iconEl && iconPosition === 'right' && iconEl}
    </button>
  );
}));

OptimizedButton.displayName = 'OptimizedButton';

export default OptimizedButton;
